import { Injectable, Inject } from '@angular/core';
import { DataAnalyticsConfigService } from '../data-analytics-config.service';
import { EnergyDataService } from '../energy-data/energy-data.service';
import { Observable, forkJoin, map, range, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { time } from '../services/global-variables.service';
import { HttpService } from '../services/http.service';
import { DemoAccountService } from '../demo-account.service';
import { LdsEventBusService } from '../services/lds-event-bus.service';

@Injectable({
  providedIn: 'root'
})
export class DataAnalyticsService {
  constructor(private eventBus: LdsEventBusService, private http: HttpService, private demo: DemoAccountService,
    private energyData: EnergyDataService, @Inject(DataAnalyticsConfigService) private config) { }

  public count: number = 0;

  /**
   * Build the query URL for energy data
   */
  private buildEnergyQueryUrl(featureId: string): string {
    if (this.demo.isDemo()) {
      return `${this.config.demoUrl}feature/energy/${featureId}?range=monthly`;
    }
    
    let end_time = Date.now();
    end_time += (time.day - end_time % time.day);
    const start_time = end_time - time.day * 30;
    
    let queryString = this.config.url + '/feature/energy/' + featureId;
    queryString += '?end_time=' + end_time;
    queryString += '&start_time=' + start_time;
    queryString += '&period=' + time.day;
    queryString += '&limit=' + 30;
    
    return queryString;
  }

  /**
   * Returns a pure HTTP Observable without any processing.
   * This ensures forkJoin can fire all requests in parallel.
   */
  private getEnergyDataObservable(featureSet: any): Observable<{ featureSet: any, data: any }> {
    const queryString = this.buildEnergyQueryUrl(featureSet.featureId);
    
    return this.http.getRequest(queryString).pipe(
      map((data: any) => ({ featureSet, data })),
      catchError(err => {
        console.error(`Error loading energy data for ${featureSet.featureId}:`, err);
        // Return null data to not block other requests
        return of({ featureSet, data: [] });
      })
    );
  }

  /**
   * @deprecated Use loadEnergySummaryData which now uses parallel Observable execution
   */
  async getDailyEnergyByRange(featureSet): Promise<any> {
    const queryString = this.buildEnergyQueryUrl(featureSet.featureId);
    
    return new Promise((resolve) => {
      this.http.getRequest(queryString).subscribe(async (res: any) => {
        this.count++;
        await this.energyData.addMonthlyDataAndDeriveWeekly(featureSet, res);
        resolve(true);
      });
    });
  }

  async getECO7(featureId) {
    let queryString = this.config.url + '/feature/energy/' + featureId;
    let end_time = Date.now();
    end_time -= end_time % time.day;

    queryString += '?end_time=' + end_time;
    queryString += '&period=' + time.hour;
    queryString += '&limit=' + 24;

    return this.http.getRequest(queryString);
  }

  getHourlyAverage(featureId, endTime?): Observable<any> {
    let queryString
    if (this.demo.isDemo()) {
      queryString = `${this.config.demoUrl}feature/energy/${featureId}?range=hourly`
    } else {
      queryString = this.config.url + '/feature/energy/' + featureId;
      const end_time = endTime ? endTime : Date.now();
      let start_time = end_time - time.day;

      queryString += '?start_time=' + start_time;
      queryString += '&end_time=' + end_time;
      queryString += '&period=' + time.hour;
      queryString += '&limit=' + 24;
    }

    return this.http.getRequest(queryString);
  }

  getMovement(featureId, range, start_time?, end_time?): Observable<any> {
    let queryString;
    if (this.demo.isDemo()) {
      const rangeNumber = range === 'weekly' ? 'weekly' : 'monthly';
      queryString = `${this.config.demoUrl}feature/movement/${featureId}?range=${rangeNumber}`
    } else {
      queryString = this.config.url + '/feature/movement/' + featureId;
      const rangeNumber = range === 'weekly' ? 7 : 30;
      queryString += '?order_asc=false';

      if (!end_time) {
        end_time = Date.now();
      }
      if (!start_time) {
        start_time = end_time - time.day * rangeNumber;
      }

      queryString += '&end_time=' + end_time;
      queryString += '&start_time=' + start_time;
    }
    return this.http.getRequest(queryString);
  }

  getDailyAverage(featureId) {
    let queryString
    if (this.demo.isDemo()) {
      queryString = `${this.config.demoUrl}feature/period/${featureId}?t=0`
    } else {
      // queryString = this.config.url.replace("v3", "v2") + '/feature/period/' + featureId;
      queryString = this.config.url + '/feature/period/' + featureId;
      let end_time = Date.now();
      end_time += (86400000 - end_time % 86400000);

      queryString += '?end_time=' + end_time;
      queryString += '&period=' + time.day;
      queryString += '&limit=' + 15;
    }
    return this.http.getRequest(queryString);
  }

  getTotalOnTime(featureId, timespan = 'weekly', startTime?, endTime?, range?) {
    let queryString;
    if (this.demo.isDemo()) {
      const range = timespan === 'weekly' ? 'weekly' : 'monthly';
      queryString = `${this.config.demoUrl}feature/duration/${featureId}?range=${range}`
    } else {
      const queryRange = range ? range : timespan === 'weekly' ? 7 : 30;
      let end_time = endTime ? endTime : Date.now();
      let start_time = startTime ? startTime : end_time - time.day * queryRange;
      // queryString = this.config.url.replace("v3", "v2") + '/feature/duration/' + featureId;
      queryString = this.config.url + '/feature/duration/' + featureId;
      queryString += '?start_time=' + start_time;
      queryString += '&end_time=' + end_time;
    }
    return this.http.getRequest(queryString);
  }

  _getFeatureRawData(queryString): Observable<any> {
    const source = this.http.getRequest(queryString)

    const processed = source.pipe(
      map(data => {
        if ('timestamp' in data && 'lastTimestamp' in data) {
          data = expandFromFlattenRaw(data)
        }
        return data
      })
    )
    return processed
  }

  getFeatureRawData(featureId, start_time?, end_time?, limit?, order_asc?): Observable<any> {
    let queryString;
    if (this.demo.isDemo()) {
      queryString = `${this.config.demoUrl}feature/raw/${featureId}?range=weekly`
    } else {
      queryString = this.config.url + '/feature/raw/' + featureId;

      if (order_asc) queryString += '?order_asc=true';
      else queryString += '?order_asc=false';

      if (!end_time) {
        end_time = Date.now();
      }
      if (!start_time) {
        start_time = end_time - time.week;
      }

      queryString += '&end_time=' + end_time;
      queryString += '&start_time=' + start_time;
      if (limit) {
        queryString += `&limit=${limit}`
      }

      queryString += '&start_append=true';
      queryString += '&end_append=true';
    }

    return this._getFeatureRawData(queryString)
  }


  getPagedRawData(last_result, featureId, page_size, start_time?, end_time?) {
    let observe = this.getFeatureRawData(featureId, start_time, end_time, page_size, true);

    observe.subscribe(res => {
      if (res.length >= page_size) {
        if (!last_result) {
          last_result = res;
        } else {
          last_result = last_result.concat(res);
        }


        return this.getPagedRawData(last_result, featureId, page_size, res[res.length - 1].timestamp, end_time);
      } else {

        if (!last_result) {
          last_result = res;
        } else {
          last_result = last_result.concat(res);
        }

        this.eventBus.broadcast("energy-monitor-data-loaded", last_result);
        return;
      }
    });
  }



  getFeatureRawDateRange(featureId, range, start_time?, end_time?, source?): Observable<any> {
    let queryString;
    if (this.demo.isDemo()) {
      let rangeNumber;
      if (range === "weekly") {
        rangeNumber = 7;
      } else if (range === "monthly") {
        rangeNumber = 30;
      } else {
        rangeNumber = range;
      }
      queryString = `${this.config.demoUrl}feature/raw/${featureId}?range=${rangeNumber}`
    } 
    else {
      queryString = this.config.url + '/feature/raw/' + featureId;
      const rangeNumber = range === 'weekly' ? 7 : 30;
      queryString += '?order_asc=false';

      if (!end_time) {
        end_time = Date.now();
      }
      if (!start_time) {
        start_time = end_time - time.day * rangeNumber;
      }

      queryString += '&end_time=' + end_time;
      queryString += '&start_time=' + start_time;

      queryString += '&start_append=true';
      queryString += '&end_append=true';
    }

    return this._getFeatureRawData(queryString)
  }

  getPagedData(last_result, featureId, page_size, start_time, end_time, endpoint?): Promise<any> {
    return new Promise<any>((resolve) => {
      let observe;
      switch (endpoint || 'raw') {
        case 'energy':
          observe = this.http.getRequest(this.config.url + '/feature/energy/' + featureId + '?start_time=' + start_time + '&end_time=' + end_time + '&limit=' + page_size + '&order_asc=true' + '&period=' + time.day + `&range=${this.getDaysBetween(start_time, end_time)}`);
          break;

        case 'raw':
        default:
          observe = this.getFeatureRawData(featureId, start_time, end_time, page_size, true);
      }

      observe.subscribe(res => {
        if (res.length >= page_size) {
          if (!last_result) {
            last_result = res;
          } else {
            last_result = last_result.concat(res);
          }
          resolve(this.getPagedData(last_result, featureId, page_size, res[res.length - 1].timestamp, end_time, endpoint));
        } else {
          if (!last_result) {
            last_result = res;
          } else {
            last_result = last_result.concat(res);
          }
          resolve(last_result);
        }
      });
    });


  }
  async getCustomDateRange(featureId, range: number, start_time, end_time, endpoint = 'period', period = time.day) {
    let queryString
    if (this.demo.isDemo()) {
      queryString = `${this.config.demoUrl}feature/${endpoint}/${featureId}?t=0`
    } else {
      queryString = this.config.url + `/feature/${endpoint}/` + featureId;
      queryString += '?start_time=' + start_time
      queryString += '&end_time=' + end_time;

      queryString += '&period=' + period;

      queryString += '&limit=' + range;
    }
    return new Promise<any>((resolve) => {
      this.http.getRequest(queryString).subscribe(res => {
        resolve(res);
      });
    });
  }

  getDaysBetween(start: number, end: number) {
    let diffTime;
    if (start > end) {
      diffTime = Math.abs(start - end);
    } else {
      diffTime = Math.abs(end - start);
    }
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  loadEnergySummaryData(energyFeatureSetsToLoad: any[] | Record<string, any>) {
    // Handle both array and object inputs
    const featureSets: any[] = Array.isArray(energyFeatureSetsToLoad) 
      ? energyFeatureSetsToLoad 
      : Object.values(energyFeatureSetsToLoad);
    
    if (featureSets.length === 0) {
      this.eventBus.broadcast('energySummary-featureSets-loaded', true);
      return;
    }

    // Create array of pure HTTP Observables - these fire in parallel with forkJoin
    const requests$ = featureSets.map((featureSet: any) => 
      this.getEnergyDataObservable(featureSet)
    );

    // forkJoin subscribes to ALL observables at once, firing parallel HTTP requests
    // Processing happens AFTER all requests complete to avoid blocking
    forkJoin(requests$).subscribe({
      next: async (results: { featureSet: any, data: any }[]) => {
        // Process all results after ALL HTTP requests have completed
        for (const result of results) {
          this.count++;
          await this.energyData.addMonthlyDataAndDeriveWeekly(result.featureSet, result.data);
        }
        this.eventBus.broadcast('energySummary-featureSets-loaded', true);
      },
      error: (err) => {
        console.error('Error loading energy summary data:', err);
        // Still broadcast completion so UI doesn't hang
        this.eventBus.broadcast('energySummary-featureSets-loaded', true);
      }
    });
  }

}



const expandFromFlattenRaw = (data) => {
  // Check if the data is in the correct format.
  if (!('timestamp' in data) || !('lastTimestamp' in data) || !('t' in data) || !('v' in data)) {
    console.log('expandFromFlattenRaw - error - Invalid data format.', data);
    throw new Error('Invalid data format.');
  }

  // Calculate the input data.
  const inputData = [];
  let timestamp = data.timestamp
  for (let i = 0; i < data.t.length; i++) {
    if (data.order === 'asc') timestamp += data.t[i]
    else timestamp -= data.t[i]

    inputData.push({
      timestamp,
      value: data.v[i]
    })
  }

  return inputData;
};
